import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'

import type { NotificationDetailModalProps } from 'features/notifications/types'

const MODAL_WIDTH = 700

/**
 * Pure-render modal for expanding a BULK_JOB notification.
 *
 * Receives the selected `NotificationDto` (whose `detail` is a populated
 * `BulkJobDetailDto` for BULK_JOB rows) and shows the per-category email
 * breakdown the bell row can't fit inline. Closes via the Antd `X`, the
 * close button, or by `null`-ing the container's selection.
 *
 * The view does NOT call service hooks, react-query, or the store — the
 * container wires those and passes the selected notification down. Per
 * `view-component-purity`, this file is free of `use*` data imports.
 *
 * Matches the team's `PostDetailModal` shape (`width=700`, centered,
 * `body height: 80vh, overflowY: scroll`) so the bell modal feels native
 * alongside the feedback-page modal the operator is already used to.
 */
export const NotificationDetailModal = ({
  notification,
  onClose,
}: NotificationDetailModalProps): JSX.Element => {
  const detail = notification?.detail ?? null
  const isOpen = notification !== null && detail !== null
  const isFailed = notification?.template === 'BULK_JOB_FAILED'

  const handleOpenLink = (): void => {
    if (notification?.link) {
      window.open(notification.link, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <PopoverModal
      isOpen={isOpen}
      onClose={onClose}
      width={MODAL_WIDTH}
      footer={null}
      title={
        notification === null ? null : (
          <ModalTitleRow>
            <ModalTitle>{notification.title}</ModalTitle>
            <Chiclet $tone={isFailed ? 'danger' : 'emerald'}>
              {isFailed ? 'failed' : 'completed'}
            </Chiclet>
          </ModalTitleRow>
        )
      }
      data-testid="notifications-detail-modal"
      content={
        detail === null ? null : (
          <Body>
            <SummaryRow data-testid="notifications-detail-summary">
              <SummaryCell>
                <SummaryNumber>{detail.newly_created_count}</SummaryNumber>
                <SummaryLabel>added</SummaryLabel>
              </SummaryCell>
              <SummaryCell>
                <SummaryNumber>{detail.skipped_existing_count}</SummaryNumber>
                <SummaryLabel>already existed</SummaryLabel>
              </SummaryCell>
              <SummaryCell>
                <SummaryNumber>{detail.failed_emails.length}</SummaryNumber>
                <SummaryLabel>failed</SummaryLabel>
              </SummaryCell>
              <SummaryCell>
                <SummaryNumber>{detail.emails_count}</SummaryNumber>
                <SummaryLabel>total</SummaryLabel>
              </SummaryCell>
            </SummaryRow>

            {detail.error_message !== null && detail.error_message !== '' && (
              <ErrorBox data-testid="notifications-detail-error">
                {detail.error_message}
              </ErrorBox>
            )}

            <EmailListSection
              label="Newly connected"
              emails={detail.newly_connected_emails}
              testId="notifications-detail-newly-connected"
            />
            <EmailListSection
              label="Already connected"
              emails={detail.already_connected_emails}
              testId="notifications-detail-already-connected"
            />
            <EmailListSection
              label="Failed"
              emails={detail.failed_emails}
              testId="notifications-detail-failed"
              tone="error"
            />
            <EmailListSection
              label="Missing"
              emails={detail.missing_emails}
              testId="notifications-detail-missing"
              tone="warning"
            />

            {/* Post-write verification = the job re-reads the hub roster
              after writing and confirms the connected set matches the
              intended set. An engineering signal — only worth surfacing
              when it FAILED (feedback: "probably not necessary to show"). */}
            {!detail.verification_passed && (
              <VerificationRow data-testid="notifications-detail-verification">
                <VerificationLabel>
                  Verification did not pass — some members may not have been
                  connected as intended
                </VerificationLabel>
                <Chiclet $tone="warning">check the Access page</Chiclet>
              </VerificationRow>
            )}

            {notification !== null && notification.link !== '' && (
              <FooterRow>
                <Button
                  variant="primary"
                  onClick={handleOpenLink}
                  data-testid="notifications-detail-cta"
                >
                  Go to Access page
                </Button>
              </FooterRow>
            )}
          </Body>
        )
      }
    />
  )
}

interface EmailListSectionProps {
  readonly label: string
  readonly emails: ReadonlyArray<string>
  readonly testId: string
  readonly tone?: 'default' | 'error' | 'warning'
}

const EmailListSection = ({
  label,
  emails,
  testId,
  tone = 'default',
}: EmailListSectionProps): JSX.Element | null => {
  // Empty categories are suppressed — keeps the modal tight when only
  // a couple of email buckets are populated. Operators see only what's
  // relevant for the run they just looked at.
  if (emails.length === 0) return null
  return (
    <Section data-testid={testId}>
      <SectionHeader>
        <SectionLabel>{label}</SectionLabel>
        <SectionCount $tone={tone}>{emails.length}</SectionCount>
      </SectionHeader>
      <EmailList>
        {emails.map((email) => (
          <EmailItem key={email} $tone={tone}>
            {email}
          </EmailItem>
        ))}
      </EmailList>
    </Section>
  )
}

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ModalTitle = styled.span`
  font-weight: 600;
  font-size: 16px;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`

const SummaryCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  background: #f7f9fc;
  border-radius: 6px;
`

const SummaryNumber = styled.span`
  font-size: 22px;
  font-weight: 600;
  color: #000000;
`

const SummaryLabel = styled.span`
  font-size: 12px;
  color: #595959;
  margin-top: 2px;
`

const ErrorBox = styled.div`
  padding: 10px 12px;
  background: #fff1f0;
  border: 1px solid #ffa39e;
  border-radius: 6px;
  color: #a8071a;
  font-size: 13px;
  white-space: pre-wrap;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SectionLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1f1f1f;
`

const SectionCount = styled.span<{ $tone: 'default' | 'error' | 'warning' }>`
  font-size: 12px;
  padding: 0 8px;
  border-radius: 10px;
  background: ${(p) =>
    p.$tone === 'error'
      ? '#fff1f0'
      : p.$tone === 'warning'
        ? '#fff7e6'
        : '#eef3fb'};
  color: ${(p) =>
    p.$tone === 'error'
      ? '#a8071a'
      : p.$tone === 'warning'
        ? '#874d00'
        : '#5072a4'};
`

const EmailList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const EmailItem = styled.li<{ $tone: 'default' | 'error' | 'warning' }>`
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  background: ${(p) =>
    p.$tone === 'error'
      ? '#fff1f0'
      : p.$tone === 'warning'
        ? '#fff7e6'
        : 'transparent'};
  color: ${(p) => (p.$tone === 'default' ? '#1f1f1f' : '#1f1f1f')};
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`

const VerificationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 6px;
`

const VerificationLabel = styled.span`
  font-size: 13px;
  color: #595959;
`

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
`
